// RankingSection.jsx
import React, { useState, useEffect } from 'react';
import { rankingService } from '../services/ranking.service';
import { useAuth } from '../contexts/AuthContext';

export default function RankingSection({ learningStats = [], loading: parentLoading = false, currentUserPoints = null }) {
  const { user, userProfile, profileLoading } = useAuth();
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh] = useState(true);

  const fetchRankings = async () => {
    try {
      setLoading(true);
      setError(null);
      const rankingData = await rankingService.getRankings();
      setRankings(rankingData);
      // setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch rankings:', err);
      setError('ランキングデータの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchRankings();
  }, []);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchRankings();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [autoRefresh]);

  useEffect(() => {
    // DataTablesの初期化
    if (window.$ && rankings.length > 0 && !loading) {
      // 既存のDataTableがあれば破棄
      if (window.$.fn.DataTable.isDataTable('#ranking-table')) {
        window.$('#ranking-table').DataTable().destroy();
      }
      
      // 新しいDataTableを初期化
      window.$('#ranking-table').DataTable({
        paging: false,
        searching: false,
        info: false,
        ordering: true,
        order: [[0, 'asc']],
        columnDefs: [
          { targets: 0, orderable: true },
          { targets: [1, 2, 3, 4], orderable: false }
        ],
        language: {
          emptyTable: 'データがありません'
        }
      });
    }
  }, [rankings, loading]);

  const getRankClass = (rank) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    return '';
  };

  const getRankChangeIcon = (change) => {
    switch (change) {
      case 'up': return <span className="arrow up">▲</span>;
      case 'down': return <span className="arrow down">▼</span>;
      default: return <span className="arrow">-</span>;
    }
  };

  const getDisplayName = (rankingUser) => {
    // 現在のユーザーの場合はプロフィールの名前表示ロジックを使用
    if (user && rankingUser.id === user.id) {
      if (profileLoading) return "読み込み中...";
      if (userProfile?.name) return userProfile.name;
      if (user?.email) return user.email.split('@')[0];
      return "ユーザー";
    }
    
    // 他のユーザーの場合は複数のフォールバックオプションを試行
    if (rankingUser.name && rankingUser.name.trim() !== '' && rankingUser.name !== 'ユーザー') {
      return rankingUser.name;
    }
    
    // デバッグ用：どんなデータが来ているかログ出力（必要時のみ有効化）
    // console.log('User data for ranking:', rankingUser);
    
    // フォールバック: ユーザーIDの一部を使用
    if (rankingUser.id) {
      return `ユーザー${rankingUser.id.slice(-4)}`;
    }
    
    return "ユーザー";
  };

  // 学習時間統計から総学習時間を計算
  const getTotalLearningTime = (rankingUser) => {
    // 現在のユーザーの場合、learningStatsから最新の学習時間を取得
    if (user && rankingUser.id === user.id && learningStats && learningStats.length > 0) {
      const totalMinutes = learningStats.reduce((sum, stat) => sum + (stat.total_minutes || 0), 0);
      return Math.floor(totalMinutes);
    }
    
    // その他のユーザーはrankingServiceから取得した値を使用
    return rankingUser.learningTime || 0;
  };

  // 現在のユーザーのポイントを取得（StatusSectionから計算されたポイントを優先）
  const getUserPoints = (rankingUser) => {
    if (user && rankingUser.id === user.id && currentUserPoints !== null) {
      return currentUserPoints;
    }
    return rankingUser.totalPoints;
  };

  if (loading) {
    return (
      <div className="ranking-section">
        <h2>ランキング</h2>
        <div className="loading-message">ランキングを読み込み中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ranking-section">
        <h2>ランキング</h2>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // const formatLastUpdated = (date) => {
  //   if (!date) return '';
  //   return `最終更新: ${date.toLocaleTimeString('ja-JP')}`;
  // };

  // const handleManualRefresh = async () => {
  //   await fetchRankings();
  // };

  // const toggleAutoRefresh = () => {
  //   setAutoRefresh(!autoRefresh);
  // };

  // 空のランキングメッセージは削除 - 全ユーザーを0ポイントで表示するため

  return (
    <div className="ranking-section">
      <div className="ranking-header">
        <h2>ランキング</h2>
      </div>
      <table id="ranking-table">
        <thead>
          <tr>
            <th id="rank-header" className="sortable">
              順位
            </th>
            <th>名前</th>
            <th>コース</th>
            <th>分数</th>
            <th>ポイント</th>
          </tr>
        </thead>
        <tbody>
          {rankings.map((user) => (
            <tr key={user.id}>
              <td data-order={user.rank}>
                <div className={`rank ${getRankClass(user.rank)}`}>{user.rank}</div>
                {getRankChangeIcon(user.rankChange)}
              </td>
              <td>
                {user.avatar && (
                  <img 
                    src={user.avatar} 
                    className={`avatar ${user.isAnonymous ? 'anonymous' : ''}`} 
                    alt="ユーザー" 
                  />
                )}
                <span className={user.isAnonymous ? 'anonymous-name' : ''}>
                  {getDisplayName(user)}
                </span>
              </td>
              <td>{user.completedCourses}</td>
              <td>{getTotalLearningTime(user)}</td>
              <td className="point">{getUserPoints(user).toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
